import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Initiative, InitiativeLink } from "@/lib/types";
import { cn } from "@/lib/utils";

interface Props {
  initiatives: Initiative[];
  links: InitiativeLink[];
  onSelect: (i: Initiative) => void;
}

const NODE_WIDTH = 220;
const NODE_HEIGHT = 76;
const NODE_GAP_Y = 16;
const LAYER_GAP_X = 80;
const PADDING = 24;

interface LaidOutNode {
  initiative: Initiative;
  x: number;
  y: number;
  layer: number;
}

export function GraphView({ initiatives, links, onSelect }: Props) {
  const blocks = useMemo(() => links.filter((l) => l.type === "blocks"), [links]);
  const related = useMemo(() => links.filter((l) => l.type === "related_to"), [links]);

  // Only show initiatives that have at least one link (graph is for relationships).
  const linkedIds = useMemo(() => {
    const set = new Set<number>();
    for (const l of links) {
      set.add(l.from_id);
      set.add(l.to_id);
    }
    return set;
  }, [links]);

  const visibleInitiatives = useMemo(
    () => initiatives.filter((i) => linkedIds.has(i.id)),
    [initiatives, linkedIds],
  );
  const unlinkedCount = initiatives.length - visibleInitiatives.length;

  // Topological layering by `blocks` edges (Kahn's algorithm). related_to ignored for layout.
  const layout = useMemo<{ nodes: LaidOutNode[]; layers: number; height: number; width: number }>(() => {
    const ids = visibleInitiatives.map((i) => i.id);
    if (ids.length === 0) return { nodes: [], layers: 0, height: 0, width: 0 };

    const idSet = new Set(ids);
    const inDegree = new Map<number, number>(ids.map((id) => [id, 0]));
    const adj = new Map<number, number[]>(ids.map((id) => [id, []]));
    for (const e of blocks) {
      if (!idSet.has(e.from_id) || !idSet.has(e.to_id)) continue;
      adj.get(e.from_id)!.push(e.to_id);
      inDegree.set(e.to_id, (inDegree.get(e.to_id) ?? 0) + 1);
    }

    const layerOfId = new Map<number, number>();
    let frontier = ids.filter((id) => (inDegree.get(id) ?? 0) === 0);
    let layer = 0;
    const visited = new Set<number>();
    while (frontier.length) {
      for (const id of frontier) {
        layerOfId.set(id, layer);
        visited.add(id);
      }
      const next: number[] = [];
      for (const u of frontier) {
        for (const v of adj.get(u) ?? []) {
          const d = (inDegree.get(v) ?? 0) - 1;
          inDegree.set(v, d);
          if (d === 0 && !visited.has(v)) next.push(v);
        }
      }
      frontier = next;
      layer++;
    }
    // Cycles or disconnected: assign remaining to last layer
    for (const id of ids) {
      if (!layerOfId.has(id)) layerOfId.set(id, layer);
    }
    const totalLayers = Math.max(...[...layerOfId.values()]) + 1;

    // Group by layer
    const perLayer: Initiative[][] = Array.from({ length: totalLayers }, () => []);
    for (const i of visibleInitiatives) {
      perLayer[layerOfId.get(i.id)!].push(i);
    }
    // Within layer: sort by team color (group same teams together), then title
    for (const arr of perLayer) {
      arr.sort((a, b) => {
        if (a.team_id !== b.team_id) return a.team_name.localeCompare(b.team_name);
        return a.title.localeCompare(b.title);
      });
    }

    const maxRows = Math.max(...perLayer.map((l) => l.length));
    const nodes: LaidOutNode[] = [];
    for (let li = 0; li < totalLayers; li++) {
      const items = perLayer[li];
      // Center each layer vertically relative to maxRows
      const layerTotalH = items.length * NODE_HEIGHT + (items.length - 1) * NODE_GAP_Y;
      const maxLayerH = maxRows * NODE_HEIGHT + (maxRows - 1) * NODE_GAP_Y;
      const yStart = PADDING + (maxLayerH - layerTotalH) / 2;
      for (let i = 0; i < items.length; i++) {
        nodes.push({
          initiative: items[i],
          layer: li,
          x: PADDING + li * (NODE_WIDTH + LAYER_GAP_X),
          y: yStart + i * (NODE_HEIGHT + NODE_GAP_Y),
        });
      }
    }
    const width = PADDING * 2 + totalLayers * NODE_WIDTH + (totalLayers - 1) * LAYER_GAP_X;
    const height = PADDING * 2 + maxRows * NODE_HEIGHT + (maxRows - 1) * NODE_GAP_Y;
    return { nodes, layers: totalLayers, width, height };
  }, [visibleInitiatives, blocks]);

  // Quick lookup id → laid-out node
  const nodeById = useMemo(() => {
    const m = new Map<number, LaidOutNode>();
    for (const n of layout.nodes) m.set(n.initiative.id, n);
    return m;
  }, [layout]);

  if (visibleInitiatives.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-12 text-center text-sm text-muted-foreground">
        No dependencies linked yet. Open an initiative and add a "blocks" or "related to" link from the detail panel.
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <LegendItem color="hsl(var(--priority-p0))">blocks</LegendItem>
          <LegendItem color="hsl(var(--muted-foreground))" dashed>
            related to
          </LegendItem>
        </div>
        <div className="text-xs text-muted-foreground">
          {visibleInitiatives.length} linked · {layout.layers} layer{layout.layers === 1 ? "" : "s"}
          {unlinkedCount > 0 && (
            <span className="ml-2">· {unlinkedCount} unlinked hidden</span>
          )}
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <div
          className="relative"
          style={{ width: `${layout.width}px`, height: `${layout.height}px`, minWidth: "100%" }}
        >
          {/* Edges */}
          <svg
            className="absolute inset-0 pointer-events-none"
            width={layout.width}
            height={layout.height}
            style={{ overflow: "visible" }}
          >
            <defs>
              <marker
                id="g-arrow-blocks"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M0,0 L10,5 L0,10 Z" fill="hsl(var(--priority-p0))" opacity="0.9" />
              </marker>
              <marker
                id="g-arrow-related"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto-start-reverse"
              >
                <path d="M0,0 L10,5 L0,10 Z" fill="hsl(var(--muted-foreground))" opacity="0.6" />
              </marker>
            </defs>

            {blocks.map((l) => {
              const a = nodeById.get(l.from_id);
              const b = nodeById.get(l.to_id);
              if (!a || !b) return null;
              return <Edge key={`b-${l.id}`} a={a} b={b} kind="blocks" />;
            })}
            {related.map((l) => {
              const a = nodeById.get(l.from_id);
              const b = nodeById.get(l.to_id);
              if (!a || !b) return null;
              return <Edge key={`r-${l.id}`} a={a} b={b} kind="related" />;
            })}
          </svg>

          {/* Nodes */}
          {layout.nodes.map((n) => (
            <GraphNode key={n.initiative.id} node={n} onClick={() => onSelect(n.initiative)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Edge({ a, b, kind }: { a: LaidOutNode; b: LaidOutNode; kind: "blocks" | "related" }) {
  // Same-layer (related_to) → straight; otherwise → bezier
  const x1 = a.x + NODE_WIDTH;
  const y1 = a.y + NODE_HEIGHT / 2;
  const x2 = b.x;
  const y2 = b.y + NODE_HEIGHT / 2;
  const dx = Math.max(40, (x2 - x1) / 2);
  const sameLayer = a.layer === b.layer;
  const isBlock = kind === "blocks";
  const path = sameLayer
    ? // Curve from right side of a to left of b, but they're in the same column → loop above
      `M ${a.x + NODE_WIDTH / 2} ${a.y} Q ${(a.x + b.x) / 2 + NODE_WIDTH / 2} ${
        Math.min(a.y, b.y) - 40
      }, ${b.x + NODE_WIDTH / 2} ${b.y}`
    : `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;

  return (
    <path
      d={path}
      fill="none"
      stroke={isBlock ? "hsl(var(--priority-p0))" : "hsl(var(--muted-foreground))"}
      strokeOpacity={isBlock ? 0.75 : 0.5}
      strokeWidth={isBlock ? 1.8 : 1.4}
      strokeDasharray={isBlock ? undefined : "5 4"}
      markerEnd={`url(#${isBlock ? "g-arrow-blocks" : "g-arrow-related"})`}
    />
  );
}

function GraphNode({ node, onClick }: { node: LaidOutNode; onClick: () => void }) {
  const i = node.initiative;
  const dim = i.status === "done" || i.status === "cancelled";
  const priorityCls =
    i.priority === "P0"
      ? "bg-priority-p0"
      : i.priority === "P1"
      ? "bg-priority-p1"
      : "bg-priority-p2";
  return (
    <button
      onClick={onClick}
      className={cn(
        "absolute rounded-lg border p-3 text-left transition-all",
        "hover:scale-[1.03] hover:shadow-xl hover:z-10",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        dim && "opacity-60",
      )}
      style={{
        left: node.x,
        top: node.y,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        borderColor: `${i.team_color}88`,
        backgroundColor: `${i.team_color}1a`,
      }}
      title={`${i.team_name} · ${i.product_name} · ${i.quarter}`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", priorityCls)} />
        <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color: i.team_color }}>
          {i.team_name}
        </span>
        <span className="text-[10px] text-muted-foreground ml-auto">{i.quarter.slice(-2)}</span>
      </div>
      <div className="text-xs font-semibold leading-tight line-clamp-2">{i.title}</div>
      <div className="text-[10px] text-muted-foreground mt-1 truncate">
        {i.product_name}
        {i.owner ? ` · ${i.owner}` : ""}
      </div>
    </button>
  );
}

function LegendItem({
  color,
  dashed,
  children,
}: {
  color: string;
  dashed?: boolean;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <svg width="20" height="8">
        <line
          x1="0"
          y1="4"
          x2="20"
          y2="4"
          stroke={color}
          strokeWidth="2"
          strokeDasharray={dashed ? "4 3" : undefined}
        />
      </svg>
      {children}
    </span>
  );
}
